/**
 * Municípios habilitados a emitir NFS-e pelo app, chaveados por IBGE.
 *
 * Fonte: lista oficial de adesões do Sefin Nacional (gov.br,
 * municipiosaderentes20260710.xlsx) cruzada com o cadastro de municípios do IBGE
 * (localidades) para obter o código IBGE de 7 dígitos — o arquivo oficial só tem
 * CNPJ + nome/UF. Gerada offline; ver src/data/fiscal/municipios_nacionais.json.
 *
 * O "portão" de emissão é `AderenteEmissorNacional` (não a mera adesão ao ADN):
 * só esses municípios delegam a emissão ao Emissor Público Nacional. São Paulo e
 * Fortaleza aderiram ao ADN mas mantêm sistema PRÓPRIO (provider dedicado). Um
 * município não listado aqui NÃO emite pelo app (avisar no onboarding).
 */

export type NfseProvider = 'nacional' | 'sao_paulo' | 'fortaleza';

export interface MunicipioNacional {
  ibge: string;
  nome: string;
  uf: string;
  provider: NfseProvider;
  emissor: boolean; // AderenteEmissorNacional (emite pelo Padrão Nacional)
  adn: boolean; // AderenteAmbienteNacional (compartilha dados ao ADN)
  situacao: string | null; // 'Ativo' | 'Cadastrado' | null
  vigencia: string | null; // início de vigência (dd/mm/aaaa)
}

let cache: MunicipioNacional[] | null = null;
let cacheTodos: MunicipioNacional[] | null = null;

/** Carrega (lazy) o dataset bundlado. Cacheado após a 1ª chamada. */
export async function loadMunicipiosNacionais(): Promise<MunicipioNacional[]> {
  if (cache) return cache;
  const mod = await import('@/data/fiscal/municipios_nacionais.json');
  const data = (mod as { default?: { municipios: MunicipioNacional[] }; municipios?: MunicipioNacional[] });
  cache = (data.default?.municipios ?? data.municipios ?? []) as MunicipioNacional[];
  return cache;
}

interface MunicipioIbgeRow {
  ibge: string;
  nome: string;
  uf: string;
}

/**
 * Carrega (lazy) TODOS os municípios do Brasil (IBGE), mapeados como provider
 * 'nacional'. Usado no onboarding do MEI: a NFS-e do MEI é emitida pelo Padrão
 * Nacional em 100% dos municípios — inclusive SP/Fortaleza — então o portão de
 * adesão (`emissor`) NÃO se aplica ao MEI. Dataset ~285KB, cacheado.
 */
export async function loadTodosMunicipios(): Promise<MunicipioNacional[]> {
  if (cacheTodos) return cacheTodos;
  const mod = await import('@/data/fiscal/municipios_todos.json');
  const data = mod as { default?: { municipios: MunicipioIbgeRow[] }; municipios?: MunicipioIbgeRow[] };
  const rows = data.default?.municipios ?? data.municipios ?? [];
  cacheTodos = rows.map((m) => ({
    ibge: m.ibge,
    nome: m.nome,
    uf: m.uf,
    provider: 'nacional' as const,
    emissor: true,
    adn: true,
    situacao: null,
    vigencia: null,
  }));
  return cacheTodos;
}

const norm = (s: string) =>
  s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim();

/** Busca por nome (substring, sem acento) e/ou UF. Vazio até 2 chars. */
export function searchMunicipios(
  list: MunicipioNacional[],
  query: string,
  limit = 50,
): MunicipioNacional[] {
  const q = norm(query);
  if (q.length < 2) return [];
  const out: MunicipioNacional[] = [];
  for (const m of list) {
    if (norm(`${m.nome} ${m.uf}`).includes(q)) {
      out.push(m);
      if (out.length >= limit) break;
    }
  }
  return out;
}

/** Siglas de UF válidas (26 estados + DF), para desempate de homônimos. */
const UF_SET = new Set([
  'AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 'MT', 'MS', 'MG',
  'PA', 'PB', 'PR', 'PE', 'PI', 'RJ', 'RN', 'RS', 'RO', 'RR', 'SC', 'SP', 'SE', 'TO',
]);

/**
 * Extrai uma sigla de UF de um texto livre — ex.: "Rua X - São Paulo/SP" → "SP".
 * Só considera um token de 2 letras ISOLADO (`\b..\b`) que seja UF válida, então
 * não confunde pedaços de palavras ("PA" de "PAULO") com estado. null se nenhum.
 */
export function parseUf(text: string | null | undefined): string | null {
  if (!text) return null;
  const tokens = text.toUpperCase().match(/\b[A-Z]{2}\b/g);
  if (!tokens) return null;
  return tokens.find((t) => UF_SET.has(t)) ?? null;
}

/**
 * Sugere um município a partir do nome de uma cidade (ex.: companies.pix_city),
 * por correspondência EXATA de nome (sem acento/caixa). Homônimos em UFs
 * diferentes são desempatados por `ufHint`; sem hint, retorna o primeiro. Retorna
 * null se nada casar EXATAMENTE (evita sugestão ruim por match parcial). É só uma
 * sugestão — o usuário confirma antes de aplicar.
 */
export function suggestMunicipio(
  list: MunicipioNacional[],
  city: string | null | undefined,
  ufHint?: string | null,
): MunicipioNacional | null {
  const c = norm(city ?? '');
  if (c.length < 2) return null;
  const exact = list.filter((m) => norm(m.nome) === c);
  if (exact.length === 0) return null;
  if (ufHint) {
    const uf = ufHint.toUpperCase();
    const byUf = exact.find((m) => m.uf === uf);
    if (byUf) return byUf;
  }
  return exact[0];
}

/**
 * Sugestão de município a partir dos campos do cadastro (pix_city + address).
 * Testa a cidade do PIX primeiro; se não casar, tenta cada fragmento do endereço
 * (split por `, - / \n`) — sempre por nome EXATO, então "Rua São Paulo" NÃO casa,
 * só o fragmento que é de fato um município ("Campinas", "São Paulo"...). A UF
 * achada no texto desempata homônimos. Pura → testável sem React.
 */
export function suggestMunicipioFromText(
  list: MunicipioNacional[],
  pixCity: string | null | undefined,
  address: string | null | undefined,
): MunicipioNacional | null {
  const candidates = [pixCity ?? '', ...(address ?? '').split(/[,\-/\n]/)]
    .map((s) => s.trim())
    .filter((s) => s.length >= 2);
  if (candidates.length === 0) return null;
  const ufHint = parseUf(`${pixCity ?? ''} ${address ?? ''}`);
  for (const c of candidates) {
    const s = suggestMunicipio(list, c, ufHint);
    if (s) return s;
  }
  return null;
}

/** Município habilitado por IBGE (null se não emite pelo app). */
export function findMunicipioByIbge(
  list: MunicipioNacional[],
  ibge: string | null | undefined,
): MunicipioNacional | null {
  if (!ibge) return null;
  const d = String(ibge).replace(/\D/g, '');
  return list.find((m) => m.ibge === d) ?? null;
}

/** Resolve o provider pelo IBGE (null = não habilitado). */
export function resolveProviderByIbge(
  list: MunicipioNacional[],
  ibge: string | null | undefined,
): NfseProvider | null {
  return findMunicipioByIbge(list, ibge)?.provider ?? null;
}
